import { create } from 'zustand'
import { Client, CreateClientDto, UpdateClientDto } from '@/types'
import { clientsService } from '@/services/clients.service'

interface ClientsState {
  clients: Client[] | null
  isInitialized: boolean
  fetchClients: () => Promise<void>
  addClient: (client: CreateClientDto) => Promise<Client>
  updateClient: (id: string, client: UpdateClientDto) => Promise<Client>
  enableClient: (id: string) => Promise<Client>
  disableClient: (id: string) => Promise<Client>
  blockClient: (id: string) => Promise<Client>
  unblockClient: (id: string) => Promise<Client>
  terminateClient: (id: string) => Promise<Client>
  deleteClient: (id: string) => Promise<void>
  getClientById: (id: string) => Client | undefined
}

export const useClientsStore = create<ClientsState>((set, get) => ({
  clients: null,
  isInitialized: false,

  fetchClients: async () => {
    // Only fetch if not already initialized
    if (get().isInitialized) {
      return
    }

    try {
      const data = await clientsService.getAll()
      set({ clients: data, isInitialized: true })
    } catch (error) {
      console.error('Error loading clients:', error)
      throw error
    }
  },

  addClient: async (clientData: CreateClientDto) => {
    const newClient = await clientsService.create(clientData)
    set(state => ({
      clients: [...state.clients || [], newClient],
    }))
    return newClient
  },

  updateClient: async (id: string, clientData: UpdateClientDto) => {
    const updatedClient = await clientsService.update(id, clientData)
    set(state => ({
      clients: state.clients?.map(client => (client._id === id ? updatedClient : client)),
    }))
    return updatedClient
  },

  enableClient: async (id: string) => {
    const updatedClient = await clientsService.enable(id)
    set(state => ({
      clients: state.clients?.map(client => (client._id === id ? updatedClient : client)),
    }))
    return updatedClient
  },

  disableClient: async (id: string) => {
    const updatedClient = await clientsService.disable(id)
    set(state => ({
      clients: state.clients?.map(client => (client._id === id ? updatedClient : client)),
    }))
    return updatedClient
  },

  blockClient: async (id: string) => {
    const updatedClient = await clientsService.block(id)
    set(state => ({
      clients: state.clients?.map(client => (client._id === id ? updatedClient : client)),
    }))
    return updatedClient
  },

  unblockClient: async (id: string) => {
    const updatedClient = await clientsService.unblock(id)
    set(state => ({
      clients: state.clients?.map(client => (client._id === id ? updatedClient : client)),
    }))
    return updatedClient
  },

  terminateClient: async (id: string) => {
    const updatedClient = await clientsService.terminate(id)
    set(state => ({
      clients: state.clients?.map(client => (client._id === id ? updatedClient : client)),
    }))
    return updatedClient
  },

  deleteClient: async (id: string) => {
    await clientsService.delete(id)
    set(state => ({
      clients: state.clients?.filter(client => client._id !== id),
    }))
  },

  getClientById: (id: string) => {
    return get().clients?.find(client => client._id === id)
  },
}))
